(function () {
  const TARGET_VENDOR = 'Spirig';
  const DEFAULT_GOAL_CENTS = 10000;
  const CART_ENDPOINT = `${window.Shopify?.routes?.root || '/'}cart.js`;
  const UPDATE_DELAYS_MS = [0, 50, 150, 400];
  const DRAWER_ANIMATION_DURATION_MS = 900;
  const DRAWER_ANIMATION_SLOP_MS = 2000;

  function normalizeString(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function vendorsMatch(a, b) {
    const left = normalizeString(a).toLowerCase();
    const right = normalizeString(b).toLowerCase();
    return Boolean(left && right && left === right);
  }

  function getComponents() {
    return Array.from(document.querySelectorAll('[data-agrotropic-vendor-progress]'));
  }

  function formatCHF(cents) {
    const amount = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(amount);
    } catch (e) {
      return `CHF ${amount.toFixed(2)}`;
    }
  }

  function hasProgressItemInDom() {
    return Boolean(document.querySelector('[data-agrotropic-cart-progress]'));
  }

  function hasVendorInCart(cart) {
    if (cart?.items?.length) {
      return cart.items.some((item) => vendorsMatch(item.vendor, TARGET_VENDOR));
    }

    return hasProgressItemInDom();
  }

  function readGoalCents(el) {
    const raw = parseInt(el.dataset.goalCents || '', 10);
    return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_GOAL_CENTS;
  }

  function getTrackedCents(cart) {
    if (!cart) return 0;

    if (!hasVendorInCart(cart)) return 0;

    if (cart.items?.length) {
      return cart.items.reduce((sum, item) => {
        return sum + (item.final_line_price ?? item.line_price ?? 0);
      }, 0);
    }

    return cart.total_price ?? 0;
  }

  function isDrawerComponent(el) {
    return Boolean(el.closest('cart-drawer'));
  }

  function getDrawerOpenToken() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return '0';
    return drawer.dataset.agrotropicVendorProgressOpenToken || '0';
  }

  function shouldAnimateOnDrawerOpen(el, totalCents, goalCents) {
    if (!isDrawerComponent(el)) return false;

    const token = getDrawerOpenToken();
    const lastToken = el.dataset.agrotropicVendorProgressAnimatedToken;
    const lastTotal = parseInt(el.dataset.agrotropicVendorProgressLastTotal || '', 10);
    const lastGoal = parseInt(el.dataset.agrotropicVendorProgressLastGoal || '', 10);

    const totalChanged = Number.isFinite(lastTotal) ? lastTotal !== totalCents : true;
    const goalChanged = Number.isFinite(lastGoal) ? lastGoal !== goalCents : true;

    // Animate once per drawer-open token, or when totals changed significantly.
    return lastToken !== token || totalChanged || goalChanged;
  }

  function animateDrawerProgress(el, totalCents, goalCents) {
    const fill = el.querySelector('[data-agrotropic-vendor-progress-fill]');
    const value = el.querySelector('[data-agrotropic-vendor-progress-value]');
    const hint = el.querySelector('[data-agrotropic-vendor-progress-hint]');

    if (!fill && !value) return;

    const targetProgress = clamp(goalCents > 0 ? totalCents / goalCents : 0, 0, 1);
    const targetWidth = Math.round(targetProgress * 100);
    const start = performance.now();

    // Reset to 0 so the animation is visible.
    if (fill) fill.style.width = '0%';
    if (value) value.textContent = `${formatCHF(0)} / ${formatCHF(goalCents)}`;

    const step = (now) => {
      const elapsed = now - start;
      const t = clamp(elapsed / DRAWER_ANIMATION_DURATION_MS, 0, 1);
      const eased = easeInOutCubic(t);

      const currentWidth = Math.round(targetWidth * eased);
      const currentCents = Math.round(totalCents * eased);

      if (fill) fill.style.width = `${currentWidth}%`;
      if (value) value.textContent = `${formatCHF(currentCents)} / ${formatCHF(goalCents)}`;

      if (t < 1) {
        el.dataset.agrotropicVendorProgressRaf = String(requestAnimationFrame(step));
        return;
      }

      // Final text and hint at end to avoid flicker.
      if (value) value.textContent = `${formatCHF(totalCents)} / ${formatCHF(goalCents)}`;
      if (hint) {
        const remaining = Math.max(0, goalCents - totalCents);
        hint.textContent = remaining > 0 ? `Noch ${formatCHF(remaining)} bis zum kostenlosen Versand` : 'Kostenloser Versand freigeschaltet';
      }
    };

    // Cancel any prior animation on this element.
    const priorRaf = parseInt(el.dataset.agrotropicVendorProgressRaf || '', 10);
    if (Number.isFinite(priorRaf)) cancelAnimationFrame(priorRaf);

    requestAnimationFrame(step);
  }

  function updateComponent(el, cart) {
    const goalCents = readGoalCents(el);
    const totalCents = getTrackedCents(cart);
    const visible = hasVendorInCart(cart);

    el.classList.toggle('is-visible', visible);
    el.setAttribute('aria-hidden', visible ? 'false' : 'true');
    if (!visible) return;

    const progress = Math.max(0, Math.min(1, totalCents / goalCents));
    const fill = el.querySelector('[data-agrotropic-vendor-progress-fill]');
    const value = el.querySelector('[data-agrotropic-vendor-progress-value]');
    const hint = el.querySelector('[data-agrotropic-vendor-progress-hint]');
    const progressPercent = `${Math.round(progress * 100)}%`;

    const shouldAnimate = shouldAnimateOnDrawerOpen(el, totalCents, goalCents);
    el.dataset.agrotropicVendorProgressLastTotal = String(totalCents);
    el.dataset.agrotropicVendorProgressLastGoal = String(goalCents);
    el.dataset.agrotropicVendorProgressAnimatedToken = getDrawerOpenToken();

    if (shouldAnimate) {
      animateDrawerProgress(el, totalCents, goalCents);
    } else {
      if (fill) fill.style.width = progressPercent;
      if (value) value.textContent = `${formatCHF(totalCents)} / ${formatCHF(goalCents)}`;
    }

    if (hint) {
      const remaining = Math.max(0, goalCents - totalCents);
      hint.textContent = remaining > 0 ? `Noch ${formatCHF(remaining)} bis zum kostenlosen Versand` : 'Kostenloser Versand freigeschaltet';
    }
  }

  async function fetchCart() {
    const response = await fetch(`${CART_ENDPOINT}?timestamp=${Date.now()}`, {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Failed to fetch cart');
    return await response.json();
  }

  let updatePromise = null;

  async function resolveCart(cartData) {
    if (updatePromise) return updatePromise;

    updatePromise = (async () => {
      try {
        return await fetchCart();
      } catch (e) {
        if (cartData?.items) return cartData;
        throw e;
      } finally {
        updatePromise = null;
      }
    })();

    return updatePromise;
  }

  async function updateAll(cartData) {
    const components = getComponents();
    if (!components.length) return;

    let cart = cartData;
    try {
      cart = await resolveCart(cartData);
    } catch (e) {
      if (!cart) return;
    }

    components.forEach((el) => updateComponent(el, cart));
  }

  const scheduledUpdates = new Set();

  function scheduleUpdate(cartData) {
    UPDATE_DELAYS_MS.forEach((delay) => {
      const timeoutId = window.setTimeout(() => {
        scheduledUpdates.delete(timeoutId);
        updateAll(cartData).catch(() => {});
      }, delay);
      scheduledUpdates.add(timeoutId);
    });
  }

  function observeCartDom() {
    const targets = [document.getElementById('CartDrawer'), document.getElementById('main-cart-items')].filter(Boolean);

    targets.forEach((target) => {
      if (target.dataset.agrotropicProgressObserved === 'true') return;
      target.dataset.agrotropicProgressObserved = 'true';

      new MutationObserver(() => {
        scheduleUpdate();
      }).observe(target, {
        childList: true,
        subtree: true,
      });
    });
  }

  function observeDrawerOpen() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    if (drawer.dataset.agrotropicVendorProgressOpenObserved === 'true') return;
    drawer.dataset.agrotropicVendorProgressOpenObserved = 'true';

    const bumpToken = () => {
      const next = String(parseInt(drawer.dataset.agrotropicVendorProgressOpenToken || '0', 10) + 1);
      drawer.dataset.agrotropicVendorProgressOpenToken = next;
      // Schedule a few updates after open; cart drawer content is swapped + animated.
      scheduleUpdate();
      window.setTimeout(() => scheduleUpdate(), 150);
      window.setTimeout(() => scheduleUpdate(), 450);
    };

    new MutationObserver(() => {
      if (drawer.classList.contains('active')) {
        const now = Date.now();
        const last = parseInt(drawer.dataset.agrotropicVendorProgressLastOpenedAt || '', 10) || 0;
        if (now - last > DRAWER_ANIMATION_SLOP_MS) {
          drawer.dataset.agrotropicVendorProgressLastOpenedAt = String(now);
          bumpToken();
        }
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function setupSubscriptions() {
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        scheduleUpdate(event?.cartData);
      });
    }

    document.addEventListener('cart:refresh', () => scheduleUpdate());
  }

  function init() {
    setupSubscriptions();
    observeCartDom();
    observeDrawerOpen();
    scheduleUpdate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
